import React, { useState, useEffect } from "react";
import Tab from "../common/Tab/Tab";
import TabItem from "../common/Tab/TabItem";
import { idfServiceIds, ezsServiceIds } from "../../config/UserServicesList";
import { useAreServicesActive } from "../../util/CommonServices";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../store";
import { useTranslation } from "react-i18next";

const CreditTab: React.FunctionComponent<{ selectedTab: string; purchasedReport?: boolean }> = (props) => {
    // Start Active service base UI Display Logic
    const isBusinessUser = useSelector((state: ApplicationState) => state.loggedInUser && state.loggedInUser.userServiceDetails?.isBusinessUser);
    const accountInfo = useSelector((state: ApplicationState) => (state.myAccountServiceState ? state.myAccountServiceState._unifiedAccountInfo : null));
    const isCreditScoreSimulatorActive = useAreServicesActive([
        idfServiceIds.creditScoreSimulator,
        ezsServiceIds.tuCreditScoreSimulator,
        ezsServiceIds.tuCanadaCreditScoreSimulator,
    ]);
    const isCreditLockActive = useAreServicesActive([
        ezsServiceIds.tuCreditLock,
        ezsServiceIds.tuCreditLockI,
        ezsServiceIds.tuCreditLock3b,
        ezsServiceIds.tuCreditLock3bI,
    ]);
    const [country, setCountry] = useState("");
    const [purchasedReport, setPurchasedReport] = useState(props.purchasedReport);

    const hasPurchaseCreditReport = useSelector((state: ApplicationState) => state.loggedInUser && state.loggedInUser.hasPurchaseCreditReport);
    const hasOneTimeReport = useSelector((state: ApplicationState) => state.creditHistoryData && state.creditHistoryData.hasOneTimeReport);
    const hasOneTimeScore = useSelector((state: ApplicationState) => state.creditHistoryData && state.creditHistoryData.hasOneTimeScore);

    useEffect(() => {
        // state already created in index.tsx call, so use that instead of calling API again
        let currentCountry = accountInfo && accountInfo.country ? accountInfo.country : "";
        if (currentCountry) {
            setCountry(currentCountry);
        } else {
            setCountry("US");
        }
        if (isCreditScoreOrTrackerActive && !isIframeReportAndScore) {
            const initialUrl = encodeURI(window.location.origin + "/app/credit/score");
            sessionStorage.setItem("initialUrl", encodeURIComponent(initialUrl));
        } else {
            const initialUrl = encodeURI(window.location.href);
            sessionStorage.setItem("initialUrl", encodeURIComponent(initialUrl));
        }
    }, []);

    useEffect(() => {}, [purchasedReport]);

    const ids = [
        ezsServiceIds.experianCreditReport,
        ezsServiceIds.experianCreditReportwithScore,
        ezsServiceIds.experianAnnualCreditReportWithScore,
        ezsServiceIds.creditReportForEachOf3Bureaus,
        ezsServiceIds.creditReportForEachOf3BureausWithScore,
        ezsServiceIds.experianAnnualCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.oneBureauQuarterlyCreditReport,
        ezsServiceIds.oneBureauMonthlyCreditReport,
        ezsServiceIds.transCreditReport,
        idfServiceIds.threeBureauCreditReports,
        idfServiceIds.transUnionCreditReport,
        ezsServiceIds.tuCreditReport,
        ezsServiceIds.tuDailyCreditReport,
        ezsServiceIds.tuMonthlyCreditReport,
        ezsServiceIds.tuQuarterlyCreditReport,
        ezsServiceIds.tuAnnualCreditReport,
        ezsServiceIds.tuCreditReportForEachOf3Bureaus,
        ezsServiceIds.tuMonthlyCreditReportForEachOf3Bureaus,
        ezsServiceIds.tuQuarterlyCreditReportForEachOf3Bureaus,
        ezsServiceIds.tuAnnualCreditReportForEachOf3Bureaus,
        ezsServiceIds.tuCreditReportWithScore,
        ezsServiceIds.tuDailyCreditReportWithScore,
        ezsServiceIds.tuMonthlyCreditReportWithScore,
        ezsServiceIds.tuQuarterlyCreditReportWithScore,
        ezsServiceIds.tuAnnualCreditReportWithScore,
        ezsServiceIds.tuOneTimeCreditReportWithScore,
        ezsServiceIds.tuCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuMonthlyCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuQuarterlyCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuAnnualCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuCanadaMonthlyCreditReportWithScore,
        ezsServiceIds.tuCanadaQuarterlyCreditReportWithScore,
        ezsServiceIds.tuCanadaAnnualCreditReportWithScore,
        ezsServiceIds.tuCanadaOneTimeCreditReportWithScore,
        ezsServiceIds.tuCanadaQuarterlyCreditReport,
    ];
    const isCreditReportActive = useAreServicesActive(ids) || isBusinessUser || (hasPurchaseCreditReport && hasOneTimeReport);
    const creditScoreAndTrackerServiceids = [
        ezsServiceIds.monthlyCreditScore,
        ezsServiceIds.quarterlyCreditScore,
        ezsServiceIds.tuDailyCreditScore,
        ezsServiceIds.tuMonthlyCreditScore,
        ezsServiceIds.tuQuarterlyCreditScore,
        ezsServiceIds.tuAnnualCreditScore,
        ezsServiceIds.tuCanadaCreditScore,
        idfServiceIds.threeBureauCreditScores,
        idfServiceIds.transUnionCreditScore,
        ezsServiceIds.tuCreditReportWithScore,
        ezsServiceIds.tuOneTimeCreditReportWithScore,
        ezsServiceIds.tuDailyCreditReportWithScore,
        ezsServiceIds.tuMonthlyCreditReportWithScore,
        ezsServiceIds.tuQuarterlyCreditReportWithScore,
        ezsServiceIds.tuAnnualCreditReportWithScore,
        ezsServiceIds.experianAnnualCreditReportWithScore,
        ezsServiceIds.tuCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuMonthlyCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuQuarterlyCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.tuAnnualCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.experianAnnualCreditReportForEachOf3BureausWithScore,
        idfServiceIds.creditScoreTracker,
        ezsServiceIds.tuCreditScoreTracker,
        ezsServiceIds.tuCanadaCreditScoreTracker,
        ezsServiceIds.expMonthlyCreditScoreTracker,
        ezsServiceIds.tuCanadaMonthlyCreditReportWithScore,
        ezsServiceIds.tuCanadaQuarterlyCreditReportWithScore,
        ezsServiceIds.tuCanadaAnnualCreditReportWithScore,
        ezsServiceIds.tuCanadaOneTimeCreditReportWithScore,
        ezsServiceIds.tuCanadaQuarterlyCreditReport,
    ];

    const iframeReportAndScoreIds = [
        ezsServiceIds.experianAnnualCreditReportForEachOf3BureausWithScore,
        ezsServiceIds.experianAnnualCreditReportWithScore,
        ezsServiceIds.creditReportForEachOf3BureausWithScore,
        ezsServiceIds.experianCreditReportwithScore,
    ];

    const isCreditScoreOrTrackerActive = useAreServicesActive(creditScoreAndTrackerServiceids) || isBusinessUser || (hasPurchaseCreditReport && hasOneTimeScore);
    const isIframeReportAndScore = useAreServicesActive(iframeReportAndScoreIds); // for these services we only get score within report iframe

    const supportInternational = useSelector(
        (state: ApplicationState) =>
            state.configurationState &&
            state.configurationState.configuration &&
            state.configurationState.configuration.partnerConfig &&
            state.configurationState.configuration.partnerConfig.supportInternational
    );
    const showCreditFreeze = supportInternational ? country === "US" : true;
    // End
    const { t: tLang } = useTranslation();

    return (
        <Tab tabId="credit-tabs">
            {isCreditScoreOrTrackerActive && !isIframeReportAndScore && (
                <TabItem title={tLang("credit.tabs.creditScore")} link="/credit/score" tabNo="1" selectedTab={props.selectedTab} />
            )}

            {isCreditReportActive && <TabItem title={tLang("credit.tabs.creditReport")} link="/credit/report" tabNo="2" selectedTab={props.selectedTab} />}

            {isCreditScoreSimulatorActive && (
                <TabItem title={tLang("credit.tabs.creditSimulator")} link="/credit/simulator" tabNo="3" selectedTab={props.selectedTab} />
            )}

            {!isBusinessUser && isCreditLockActive && (
                <TabItem title={tLang("credit.tabs.creditLock")} link="/credit/lock" tabNo="4" selectedTab={props.selectedTab} />
            )}

            {!isBusinessUser && !isCreditReportActive && hasPurchaseCreditReport && (
                <TabItem title={tLang("credit.tabs.creditReport")} link="/credit/purchase" tabNo="5" selectedTab={props.selectedTab} />
            )}

            {!isBusinessUser && showCreditFreeze && (
                <TabItem title={tLang("credit.tabs.freezeMyCredit")} link="/credit/mycredit" tabNo="6" selectedTab={props.selectedTab} />
            )}
        </Tab>
    );
};

export default CreditTab;
